<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'

import { UpdateIncident } from '@mobile/ActionQueue/Actions/Actions'
import { isIncidentEditable } from '@mobile/ActionQueue/Actions/Incidents/ProbableIncident'
import Button from '@mobile/components/Button/Button.vue'
import { ProbableLinkedIncident } from '@mobile/views/Relation/Shared/RelationDependencies/types'
import { useEventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'
import { OnClickOutside } from '@vueuse/components'

import TimeUpdateInput from './TimeUpdateInput.vue'

type UpdateTime = {
  minute: string
  second: string
}
type Props = {
  linkedIncident: ProbableLinkedIncident
  shouldFocusOnMinutesInput?: boolean
  docked?: boolean
  pinned?: boolean
}
const props = defineProps<Props>()
const { actionQueue, probableEvent, sport } = useEventInjections()
const incident = computed(() => props.linkedIncident.incident)
const minuteInputRef = ref<HTMLInputElement | null>(null)
const currentTime = computed<UpdateTime>(() => {
  const [minute, second] = incident.value.event_time?.split(':') ?? ['00', '00']

  return {
    minute,
    second,
  }
})
const updateTime = ref<UpdateTime>({
  minute: currentTime.value.minute,
  second: currentTime.value.second,
})

const editable = ref(false)
const shouldBeSelected = ref(false)
const isEditable = computed(() => isIncidentEditable(incident.value, sport))

watch(
  () => props.docked,
  async (docked) => {
    if (docked) {
      setUpdateTimeValues()
    } else {
      updateIncidentTime()
    }

    editable.value = !!docked

    if (props.shouldFocusOnMinutesInput) {
      const scrollX = window.scrollX
      const scrollY = window.scrollY
      await nextTick()
      minuteInputRef.value?.focus()
      window.scrollTo(scrollX, scrollY)
    }
  },
  { immediate: true },
)

watch(editable, async (editable) => {
  if (editable) {
    setUpdateTimeValues()
  }
})

function setUpdateTimeValues(): void {
  updateTime.value.minute = currentTime.value.minute
  updateTime.value.second = currentTime.value.second
}

async function edit(): Promise<void> {
  if (props.docked || props.pinned) {
    editable.value = true
  }

  shouldBeSelected.value = true

  const scrollX = window.scrollX
  const scrollY = window.scrollY

  await nextTick()
  minuteInputRef.value?.focus()
  window.scrollTo(scrollX, scrollY)
}

function shouldBeSaved(): boolean {
  return (
    Number(updateTime.value.minute) !== Number(currentTime.value.minute) ||
    Number(updateTime.value.second) !== Number(currentTime.value.second)
  )
}

function updateIncidentTime(): void {
  if (shouldBeSaved()) {
    const action = new UpdateIncident(incident.value.id, {
      event_id: probableEvent.value.id,
      incident_id: incident.value.incident_id,
      minute: Number(updateTime.value.minute),
      second: Number(updateTime.value.second),
    })

    actionQueue.value.add(action)

    editable.value = false
  }
}

function handleClickOutside(): void {
  updateIncidentTime()
}

function handleFormSubmit(): void {
  updateIncidentTime()

  editable.value = false
}
</script>

<template>
  <div
    v-if="editable"
    class="w-[150px]"
  >
    <OnClickOutside @trigger="handleClickOutside()">
      <form
        class="flex items-center space-x-1.5"
        @submit.prevent="handleFormSubmit()"
      >
        <TimeUpdateInput
          ref="minuteInputRef"
          v-model="updateTime.minute"
        />
        <div>:</div>
        <TimeUpdateInput v-model="updateTime.second" />
        <Button
          class="w-10"
          color="green"
          size="xs"
          type="submit"
        >
          <FontAwesomeIcon icon="save" />
        </Button>
      </form>
    </OnClickOutside>
  </div>
  <div
    v-else
    class="incident-time-update-button flex items-center space-x-2"
    :class="{ 'hover:text-blue group cursor-pointer': isEditable }"
    style="width: 90px"
    @click="edit"
  >
    <div>{{ currentTime.minute }}:{{ currentTime.second }}</div>
    <FontAwesomeIcon
      v-if="isEditable"
      class="mb-1 opacity-0 group-hover:opacity-100"
      icon="edit"
    />
  </div>
</template>
